import { format } from 'date-fns';
import { lighten } from 'polished';
import React, { useState } from 'react';
import { uuid } from '../../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../../Legacy';
import { Colors } from '../../../../ui';
import { RenderBox, RenderSvg } from '../../../canvas/visualization/RenderHelpers';
import { FrameBorderWidthAttr } from '../FrameAssociations';
import { TimelineFrameAddon, TimelineFrameAddonHeadings, TimelineFrameAddonLayout } from './TimelineFrameAddon';

const headStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
    fontWeight: '36px',
};

const getMonthFormatFor = (columnSize: number): string => {
    if (columnSize <= 100) return 'M';
    else if (columnSize <= 300) return 'MMM';
    else return 'MMMM';
};

type RenderLayoutProps = {
    layout: TimelineFrameAddonLayout,
    headers: TimelineFrameAddonHeadings,
    color: string,
    lineWidth: number,
};


const RenderLayout = React.memo<RenderLayoutProps>(({ layout, headers, color, lineWidth }) => {
    const { base, head, body, pattern, now } = layout;

    const [[g1]] = useState([uuid(), uuid(), uuid()]);
    const [[p1, p2, p3, p4]] = useState([uuid(), uuid(), uuid(), uuid()]);

    headers = headers.filter(x => x !== 'D');
    const headerStops = headers.flatMap((x, i) => [
        {
            id: `${x}1`,
            offset: `${(i / headers.length) * 100}%`,
            stopColor: lighten(.02 * i, color || Colors.primary),
        },
        {
            id: `${x}2`,
            offset: `${((i + 1) / headers.length) * 100}%`,
            stopColor: lighten(.02 * i, color || Colors.primary),
        }
    ]);

    return <>
        <RenderSvg area={base}>
            <defs>
                <linearGradient id={g1} x1="0" x2="0" y1="0" y2="1">
                    {headerStops.map(x => <stop key={x.id} {...x} />)}
                </linearGradient>
                <pattern id={p1} x={0} y={0} width={head.years.colWidth * 12} height={head.years.area.height} patternUnits="userSpaceOnUse" >
                    <line x1={0} y1={0} x2={0} y2="100%" stroke={Colors.gray600} strokeDasharray="5" strokeWidth={lineWidth} />
                </pattern>
                {/* <pattern id={p2} x={0} y={0} width={head.quarters.colWidth * 4} height={head.quarters.area.height} patternUnits="userSpaceOnUse" >
                <line x1={0} y1={0} x2={0} y2="100%" stroke={Colors.gray600} strokeDasharray="5" strokeWidth={lineWidth} />
            </pattern> */}
                <pattern id={p3} x={0} y={0} width={head.months.colWidth} height={head.months.area.height} patternUnits="userSpaceOnUse" >
                    <line x1={0} y1={0} x2={0} y2="100%" stroke={Colors.gray600} strokeDasharray="5" strokeWidth={lineWidth} />
                </pattern>
                <pattern id={p4} x={0} y={head.area.height} width={pattern.width} height={pattern.height} patternUnits="userSpaceOnUse" >
                    <line x1={0} y1={0} x2={0} y2="100%" stroke={Colors.gray400} strokeWidth={lineWidth} />
                    <line x1={0} y1={0} x2="100%" y2={0} stroke={Colors.gray400} strokeWidth={lineWidth} />
                </pattern>
            </defs>
            <g>
                {head.dateline && <>
                    <line x1={`${now * 100}%`} y1={head.area.bottom} x2={`${now * 100}%`} y2="100%" stroke={Colors.primary} strokeWidth="5" />
                    <circle cx={`${now * 100}%`} cy={head.area.bottom} r="10" fill={Colors.primary} />
                </>}
                <rect {...head.area.toObject('xy')} fill={`url(#${g1})`} />
                <rect {...head.years.area.toObject('xy')} fill={`url(#${p1})`} />
                <rect {...head.quarters.area.toObject('xy')} fill={`url(#${p2})`} />
                <rect {...head.months.area.toObject('xy')} fill={`url(#${p3})`} />
                <rect {...body.toObject('xy')} fill={`url(#${p4})`} />
            </g>
        </RenderSvg>
        {head.years.area.area() > 0 && head.years.cells.map(x =>
            <RenderBox key={x.date.getTime()} {...x.area.toCSS()} {...headStyle} color={Colors.contrastTextColorFor(color)}>
                {format(x.date, 'yyyy')}
            </RenderBox>
        )}
        {head.quarters.area.area() > 0 && head.quarters.cells.map(x =>
            <RenderBox key={x.date.getTime()} {...x.area.toCSS()} {...headStyle} color={Colors.contrastTextColorFor(color)}>
                {format(x.date, 'qqq')}
            </RenderBox>
        )}
        {head.months.area.area() > 0 && head.months.cells.map(x =>
            <RenderBox key={x.date.getTime()} {...x.area.toCSS()} {...headStyle} color={Colors.contrastTextColorFor(color)}>
                {format(x.date, getMonthFormatFor(x.area.width))}
            </RenderBox>
        )}
    </>;
});


export const TimelineFrameAddonVisual: LFC<{ object: TimelineFrameAddon }> = ({ object }) => {
    const { layout, headers, color } = object;

    const lineWidth = FrameBorderWidthAttr.get(object.parentFrame) / 3 * 1;

    // Force key remount of svg when width/height changes to fix a rendering bug in Chrome
    const key = `${layout.base.width}x${layout.base.height}`;

    return <RenderLayout key={key} {...{ layout, headers, color, lineWidth }} />;
};
